<template>
    <div>
        <section id="cta-section" class="mb-32 md:mb-10 pt-20 md:pt-24 bg-cover bg-right-bottom md:bg-center bg-no-repeat" :style="getStyles()">
            <div 
                class="lg:content-center m-auto text-center mt-10 xl:mt-0 pt-5 xl:pt-0 my-auto mx-auto h-full w-full md:w-3/4 lg:w-2/3 xl:w-1/2 px-4 md:px-0" 
                :class="context.isDarkMode ? 'text-silver' : 'text-void'"
            >
                <div class="text-right md:text-left mt-16 mt-0 w-full md:w-1/2 lg:w-2/3">
                    <h1 class="text-2xl md:text-4xl 2xl:text-5xl font-bold">
                        {{ translationHandler.translate('Build your digital empire.', 'ucfirst') }}
                    </h1>
                    <p class="mt-6 mb-10 text-lg md:text-lg 2xl:text-xl">
                        {{ translationHandler.translate('Every great empire begins with a vision. Tell us yours and get ready to watch your brand rise. We are here to shape your online presence into a digital masterpiece.') }}
                    </p>
                </div>

                <form 
                    id="contact-form" 
                    class="mt-6 mr-auto w-full md:w-4/5 lg:w-4/5 xl:w-3/5 p-6 rounded-2xl border"
                    :class="context.isDarkMode ? 'bg-semidark-silver border-semidark-silver' : 'bg-white border-light-white shadow text-void'"
                >
                    <InputText type="text" :class="'w-full p-2 mb-4 shadow-lg rounded'"
                        :placeholder="translationHandler.translate('your name*', 'capitalize')"
                        v-model="formEmail.name" />

                    <InputText type="email" :class="'w-full p-2 mb-4 shadow-lg rounded'"
                        :placeholder="translationHandler.translate('your email*', 'capitalize')"
                        v-model="formEmail.email" />

                    <div class="w-full mb-4">
                        <PhoneInput v-model="formEmail.phoneNumber" @selectedCountry="onCountryChange" :hasPhoneValue="formEmail.phoneNumber.length > 0 ? true : false" />
                    </div>

                    <div class="w-full py-2 mb-4 text-left">
                        <InputText type="text" :class="'w-full shadow-lg rounded'"
                            :placeholder="translationHandler.translate('title*', 'capitalize')"
                            v-model="formEmail.title" @input="setTitleSize()" />
                        <small class="mr-auto w-full" :class="context.isDarkMode ? 'text-white' : 'text-void'">{{ titleLength }} {{ translationHandler.translate('of') }}
                            250</small>
                    </div>
                    

                    <div class="w-full py-2 mb-4 text-left">
                        <Textarea v-model="formEmail.message"
                            :placeholder="translationHandler.translate('your message*', 'capitalize')" rows="5" cols="30"
                            autoResize
                            style="width: 100% !important;"
                            @input="setTextAreaSize()" />
                        <small class="mr-auto w-full" :class="context.isDarkMode ? 'text-white' : 'text-void'">{{ textAreaSize }} {{ translationHandler.translate('of') }}
                            4000</small>
                    </div>

                    <div 
                        class="flex gap-2 content-center mt-10 primary-button max-width-md grow-effect rounded-3xl px-4 shadow"
                        @click.prevent="saveSimpleBudget()"
                    >
                        <span class="text-lg lg:text-2xl my-auto text-silver font-semibold mx-auto md:mx-0">
                            {{ translationHandler.translate('send', 'ucfirst') }}
                        </span>
                        <i class="ml-auto text-silver pi pi-send border border-second-color rounded-full bg-second-color p-2 my-auto shadow"></i>
                    </div>
                </form>

            </div>
        </section>
        
        <FooterComponent />

        <Dialog v-model:visible="showSupportInformations" :style="{ width: '90%' }" :draggable="false" 
            role="dialog" aria-label="Informação de erro"
            :closable="false" :pt:root:class="context.isDarkMode ? '!bg-void' : '!bg-white'" pt:mask:class="backdrop-blur-sm">
            <div class="rounded-lg p-6 text-center relative" :class="context.isDarkMode ? 'bg-void' : 'bg-white'">
                <button class="absolute top-2 right-2 text-2xl md:block" :class="context.isDarkMode ? 'text-silver' : 'text-void'"
                        @click="resetSupportData()">&times;</button>
                <div class="flex flex-col items-center justify-center space-y-4" :class="context.isDarkMode ? 'text-silver' : 'text-void'">
                    <i class="pi pi-exclamation-triangle text-6xl text-red-500"></i>

                    <h2 class="text-2xl font-semibold">
                        {{ translationHandler.translate('An error occurred', 'ucfirst') }}
                    </h2>

                    <p>
                        {{ translationHandler.translate("Don't worry! Your attempt to submit your quote failed, but the form has been **safely saved** and we have already notified our support team.", 'ucfirst') }}
                        
                    </p>

                    <p class="text-sm font-light">
                        {{ translationHandler.translate('Our experts will take care of it and get back to you shortly.', 'ucfirst') }}
                    </p>
                </div>
            </div>
        </Dialog>

        <InfoDialog :title="infoDialog.title" :text="infoDialog.description" v-model:visible="infoDialog.visible" />

        <LoadingDialog v-if="isLoading" :visible="isLoading" />
    </div>
</template>

<script setup>
    import translationHandler from '@/composables/handlers/TranslationHandler';
    import { showToast } from '@/composables/toast';
    import { useContextStore } from '@/stores/context';
    import { Dialog, InputText, Textarea, useToast } from 'primevue';
    import { onMounted, onUnmounted, reactive, ref } from 'vue';
    import PhoneInput from '@/components/Utils/PhoneComponent.vue';
    import FooterComponent from '@/components/FooterComponent.vue';
    import DomHandler from '@/composables/handlers/DomHandler';
    import supportService from '@/services/SupportService';
    import utils from '@/composables/utils';
    import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
    import InfoDialog from '@/components/Utils/InfoDialog.vue';
    import urlHandler from '@/composables/urlHandler';
    import BudgetService from '@/services/BudgetService';

    const context = useContextStore();
    const toast = useToast();
    const windowWidth = ref(window.innerWidth);
    const breakPoint = 500;

    onMounted(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        isScreenSmall();
    });

    onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
    });

    function isScreenSmall(){
        return windowWidth.value > breakPoint ? false : true;
    }

    function handleResize(){
        windowWidth.value = window.innerWidth;
    }

    function getStyles() {
        let backgroundImage = require(`@/assets/images/ares_${context.isDarkMode ? 'dark' : 'light'}.webp`);

        if (isScreenSmall()) {
            backgroundImage = require(`@/assets/images/ares_small_${context.isDarkMode ? 'dark' : 'light'}.webp`);
        }

        return {
            'background-image': `url(${backgroundImage})`,
            'margin-top': '-15px'
        };
    }

    const formEmail = reactive({
        name: '',
        email: '',
        phoneNumber: '',
        title: '',
        message: '',
        selected_country: ''
    });

    const lastTextAreaValue = ref('');
    const textAreaSize = ref(0);
    function setTextAreaSize() {
        let value = formEmail.message;
        if (!value) {
            textAreaSize.value = 0;
            lastTextAreaValue.value = '';
            return;
        }

        if (value.length > 4000) {
            formEmail.message = value.slice(0, 4000);
            showToast(toast, translationHandler.translate('maximum limit of 4000 characters reached.', 'ucfirst'), 'info');
        }

        lastTextAreaValue.value = formEmail.message;
        textAreaSize.value = lastTextAreaValue.value.length;
    }

    const lastTitleValue = ref('');
    const titleLength = ref(0);
    function setTitleSize() {
        let value = formEmail.title;
        if (!value) {
            titleLength.value = 0;
            lastTitleValue.value = '';
            return;
        }

        if (value.length > 250) {
            formEmail.message = value.slice(0, 250);
            showToast(toast, translationHandler.translate('maximum limit of 250 characters reached.', 'ucfirst'), 'info');
        }

        lastTitleValue.value = formEmail.title;
        titleLength.value = lastTitleValue.value.length;
    }

    const infoDialog = reactive({
        title: '',
        description: '',
        visible: false
    });
    const isLoading = ref(false);
    async function saveSimpleBudget() {
        isLoading.value = true;

        const itemsToCheck = [
            {name: 'name', rule: 'name', required: true},
            {name: 'email', rule: 'email', required: true},
            {name: 'phoneNumber', rule: 'phone', required: false},
            {name: 'title', rule: 'title', required: true},
            {name: 'message', rule: 'description', required: true},
        ];
        for (let item of itemsToCheck) {
            const checkResult = await checkFields(item.rule, formEmail[item.name]);
            if (
                !checkResult.valid &&
                (
                    ![null, undefined, ''].includes(formEmail[item.name]) ||
                    item.required == true
                )
            ) {
                showToast(toast, translationHandler.translate(checkResult.message, 'ucfirst'), 'error');
                isLoading.value = false;
                return;
            }
        }
        
        let response = await BudgetService.saveBudget(formEmail, true);

        const messages = {
            success: 'budget sent, thank you',
            error: 'budget not sent, try again later or contact the support'
        }

        const translatedMessage = translationHandler.translate(messages[response ? 'success' : 'error'], 'ucfirst');
        
        isLoading.value = false;

        if (!response) {
            alertSuport({data: utils.copyVariable(formEmail), action: 'BudgetService.saveSimpleBudget', component: 'ContactComponent'});
        } else {
            infoDialog.title = translationHandler.translate('success', 'ucfirst');
            infoDialog.description = translatedMessage;
            infoDialog.visible = true;
        }

        resetFormEmail();
    }

    function onCountryChange(country) {
        if (country.code) {
            formEmail.selected_country = country.code;
        }
    }

    function resetFormEmail() {
        formEmail.name = '';
        formEmail.email = '';
        formEmail.phoneNumber = '';
        formEmail.title = '';
        formEmail.message = '';

        textAreaSize.value = 0;
        titleLength.value = 0;
    }

    const showSupportInformations = ref(false);
    function alertSuport(dataPayload = {}) {
        showSupportInformations.value = true;

        supportService.informSupportError(dataPayload);

        DomHandler.disableScroll();
    }

    function resetSupportData() {
        showSupportInformations.value = false;
        DomHandler.enableScroll();
    }

    async function checkFields(rule, value) {
        let data = {
            valid: true,
            message: null
        };
        
        switch (rule) {
            case 'email':
                data.valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                data.message = 'email is invalid';
                break;
            case 'phone':
                data.valid = /^\+?\d{10,15}$/.test(value.replace(/\s+/g, ''));
                data.message = 'telephone number is invalid';
                break;
            case 'title':
                data.valid = value.length > 20;
                data.message = 'the title is too short, 20 letters are required at least';
                break;
            case 'description':
                data.valid = value.length > 50;
                data.message = 'the description is too short, 50 letters are required at least';
                break;
            case 'text-area':
                data.valid = value.length > 50;
                data.message = 'tell us more about your idea, 50 characters at least';
                break;
            case 'name':
                data.valid = value.split(' ').length == 1 || value.length < 6 ? false : true;
                data.message = 'your full name is required';
                break;
            case 'url':
                if (value && value.length > 0){
                    isLoading.value = true;
                    data.valid = await urlHandler.isUrlValid(value);
                    isLoading.value = false;
                    data.message = !data.valid ? 'invalid url' : '';
                } else {
                    data.valid = true;
                }
                break;
            default:
                data.valid = true;
                break;
        }

        return data;
    }
</script>
